/**
 * University / learning-service UI contracts.
 *
 * Wire shapes mirror Pydantic schemas in
 * ``services/learning-service/src/learning_service/api/*``. Where the
 * upstream uses snake_case (`skill_id`, `composite_score`, …), the
 * gateway forwards verbatim — these schemas preserve the same casing so
 * the API client stays a pass-through.
 *
 * Adds Zod schemas for the new gateway-side wrappers (skills list,
 * tuners list, training-sessions list) shipped in Phase 8.
 */
import { z } from 'zod';

// ── Catalog / marketplace status ──────────────────────────────────────
export const skillMarketplaceStatuses = [
  'private',
  'candidate',
  'exportable',
  'published',
  'sold',
  'archived',
] as const;
export const skillMarketplaceStatusSchema = z.enum(skillMarketplaceStatuses);
export type SkillMarketplaceStatus = z.infer<typeof skillMarketplaceStatusSchema>;

export const skillExportFormats = [
  'anthropic_skill_md',
  'codex_json',
  'gemini_yaml',
  'generic_json',
] as const;
export const skillExportFormatSchema = z.enum(skillExportFormats);
export type SkillExportFormat = z.infer<typeof skillExportFormatSchema>;

export const skillExportTargets = ['internal', 'external', 'sold', 'public_marketplace'] as const;
export const skillExportTargetSchema = z.enum(skillExportTargets);
export type SkillExportTarget = z.infer<typeof skillExportTargetSchema>;

export const trainingSessionStatuses = [
  'planned',
  'active',
  'awaiting_tuner',
  'completed',
  'abandoned',
] as const;
export const trainingSessionStatusSchema = z.enum(trainingSessionStatuses);
export type TrainingSessionStatus = z.infer<typeof trainingSessionStatusSchema>;

// ── Marketplace listing (already shipped server-side) ─────────────────
export const marketplaceListingSchema = z.object({
  skill_id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  version: z.string(),
  description: z.string().nullable(),
  license_type: z.string(),
  marketplace_status: skillMarketplaceStatusSchema,
  effectiveness_score: z.number().nullable(),
  total_attempts: z.number().nullable(),
  last_export_at: z.string().nullable(),
  export_count: z.number(),
});
export type MarketplaceListing = z.infer<typeof marketplaceListingSchema>;

// ── Skill catalog entry (camelCase wrapper at gateway) ────────────────
export const skillCatalogEntrySchema = z.object({
  skillId: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  version: z.string(),
  effectivenessScore: z.number().nullable(),
  totalAttempts: z.number().nullable(),
  marketplaceStatus: skillMarketplaceStatusSchema,
  ownerDepartmentKey: z.string().nullable(),
  licenseType: z.string().nullable(),
  updatedAt: z.string(),
  exportCount: z.number(),
  lastExportAt: z.string().nullable(),
});
export type SkillCatalogEntry = z.infer<typeof skillCatalogEntrySchema>;

export const skillCatalogListSchema = z.object({
  skills: z.array(skillCatalogEntrySchema),
});
export type SkillCatalogList = z.infer<typeof skillCatalogListSchema>;

// ── Skill detail (full row) ───────────────────────────────────────────
export const skillDetailSchema = z.object({
  skillId: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  version: z.string(),
  promptTemplate: z.string(),
  systemPrompt: z.string().nullable(),
  exampleInputs: z.array(z.unknown()),
  exampleOutputs: z.array(z.unknown()),
  antiPatterns: z.array(z.string()),
  licenseType: z.string(),
  compatibleProviders: z.array(z.string()),
  minContextTokens: z.number().nullable(),
  expectedOutputTokens: z.number().nullable(),
  marketplaceStatus: skillMarketplaceStatusSchema,
  ownerDepartmentKey: z.string().nullable(),
  effectivenessScore: z.number().nullable(),
  totalAttempts: z.number().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SkillDetail = z.infer<typeof skillDetailSchema>;

// ── Skill versions timeline ───────────────────────────────────────────
export const skillVersionSchema = z.object({
  version: z.string(),
  changelog: z.string().nullable(),
  authoredById: z.string().nullable(),
  authoredByName: z.string().nullable(),
  authoredByKind: z.enum(['tuner', 'agent', 'system', 'founder']),
  effectivenessScore: z.number().nullable(),
  totalAttempts: z.number().nullable(),
  createdAt: z.string(),
  isActive: z.boolean(),
});
export type SkillVersion = z.infer<typeof skillVersionSchema>;

// ── Effectiveness (passthrough) ───────────────────────────────────────
export const effectivenessWindowSchema = z.object({
  skill_id: z.string().uuid(),
  skill_version: z.string().nullable(),
  total_attempts: z.number(),
  successes: z.number(),
  first_try_successes: z.number(),
  avg_iterations: z.number().nullable(),
  founder_approvals: z.number(),
  founder_rejects: z.number(),
  avg_cost_usd: z.number().nullable(),
  composite_score: z.number().nullable(),
  last_attempt_at: z.string().nullable(),
  window_days: z.number().nullable().optional(),
  extra: z.record(z.unknown()).nullable().optional(),
});
export type EffectivenessWindow = z.infer<typeof effectivenessWindowSchema>;

export const skillEffectivenessDetailedSchema = z.object({
  all_time: effectivenessWindowSchema,
  window_30d: effectivenessWindowSchema,
  window_90d: effectivenessWindowSchema,
});
export type SkillEffectivenessDetailed = z.infer<typeof skillEffectivenessDetailedSchema>;

export const effectivenessPointSchema = z.object({
  day: z.string(),
  attempts: z.number(),
  successes: z.number(),
  compositeScore: z.number().nullable(),
});
export type EffectivenessPoint = z.infer<typeof effectivenessPointSchema>;

// ── Tuners ────────────────────────────────────────────────────────────
export const tunerSummarySchema = z.object({
  tunerId: z.string().uuid(),
  userId: z.string(),
  displayName: z.string(),
  email: z.string().nullable(),
  scopeDepartmentKeys: z.array(z.string()),
  authority: z.record(z.unknown()).nullable(),
  activeSince: z.string().nullable(),
  editsTotal: z.number(),
  approvalsTotal: z.number(),
  rejectedTotal: z.number(),
  avgLift: z.number().nullable(),
});
export type TunerSummary = z.infer<typeof tunerSummarySchema>;

export const tunersListSchema = z.object({
  tuners: z.array(tunerSummarySchema),
});
export type TunersList = z.infer<typeof tunersListSchema>;

export const tunerEditSchema = z.object({
  id: z.string().uuid(),
  tunerId: z.string().uuid(),
  skillId: z.string().uuid(),
  skillKey: z.string(),
  skillName: z.string(),
  fromVersion: z.string(),
  toVersion: z.string(),
  diffSummary: z.string(),
  effectivenessBefore: z.number().nullable(),
  effectivenessAfter: z.number().nullable(),
  effectivenessDelta: z.number().nullable(),
  createdAt: z.string(),
});
export type TunerEdit = z.infer<typeof tunerEditSchema>;

// ── Training sessions ─────────────────────────────────────────────────
export const trainingSessionSummarySchema = z.object({
  id: z.string().uuid(),
  skillId: z.string().uuid().nullable(),
  skillKey: z.string().nullable(),
  skillName: z.string().nullable(),
  tunerId: z.string().nullable(),
  tunerName: z.string().nullable(),
  cardId: z.string().nullable(),
  cardTitle: z.string().nullable(),
  status: trainingSessionStatusSchema,
  iterationCount: z.number(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  outcome: z.enum(['approved', 'rejected', 'partial', 'aborted']).nullable(),
  markedAsExample: z.enum(['positive', 'negative', 'neutral']).nullable(),
});
export type TrainingSessionSummary = z.infer<typeof trainingSessionSummarySchema>;

export const trainingSessionsListSchema = z.object({
  sessions: z.array(trainingSessionSummarySchema),
});
export type TrainingSessionsList = z.infer<typeof trainingSessionsListSchema>;

export const trainingIterationKinds = [
  'agent_draft',
  'tuner_correction',
  'tuner_note',
  'final',
] as const;
export const trainingIterationKindSchema = z.enum(trainingIterationKinds);
export type TrainingIterationKind = z.infer<typeof trainingIterationKindSchema>;

export const trainingIterationSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().uuid(),
  index: z.number(),
  kind: trainingIterationKindSchema,
  authorId: z.string().nullable(),
  authorName: z.string().nullable(),
  authorKind: z.enum(['agent', 'tuner', 'founder']),
  content: z.string(),
  prevIterationId: z.string().nullable(),
  approved: z.boolean(),
  costUsd: z.number().nullable(),
  createdAt: z.string(),
});
export type TrainingIteration = z.infer<typeof trainingIterationSchema>;

export const trainingSessionDetailSchema = z.object({
  session: trainingSessionSummarySchema,
  iterations: z.array(trainingIterationSchema),
});
export type TrainingSessionDetail = z.infer<typeof trainingSessionDetailSchema>;

// ── Mutations ─────────────────────────────────────────────────────────
export const createTrainingSessionRequestSchema = z.object({
  skillId: z.string().uuid(),
  cardId: z.string().uuid().optional(),
  initialDraft: z.string().optional(),
  dryRun: z.boolean().default(false),
});
export type CreateTrainingSessionRequest = z.infer<typeof createTrainingSessionRequestSchema>;

export const appendIterationRequestSchema = z.object({
  sessionId: z.string().uuid(),
  kind: trainingIterationKindSchema,
  content: z.string(),
  prevIterationId: z.string().uuid().optional(),
  dryRun: z.boolean().default(false),
});
export type AppendIterationRequest = z.infer<typeof appendIterationRequestSchema>;

export const approveExampleRequestSchema = z.object({
  sessionId: z.string().uuid(),
  iterationId: z.string().uuid(),
  markedAs: z.enum(['positive', 'negative', 'neutral']).default('positive'),
  bumpSkillVersion: z.boolean().default(true),
});
export type ApproveExampleRequest = z.infer<typeof approveExampleRequestSchema>;

export const approveExampleResponseSchema = z.object({
  sessionId: z.string().uuid(),
  iterationId: z.string().uuid(),
  skillId: z.string().uuid(),
  previousVersion: z.string(),
  newVersion: z.string(),
  approvedAt: z.string(),
});
export type ApproveExampleResponse = z.infer<typeof approveExampleResponseSchema>;

// ── Export ────────────────────────────────────────────────────────────
export const exportSkillRequestSchema = z.object({
  skill_id: z.string().uuid(),
  version: z.string().optional(),
  format: skillExportFormatSchema.default('anthropic_skill_md'),
  target: skillExportTargetSchema.default('internal'),
  redact: z.boolean().nullable().optional(),
  customer_metadata: z.record(z.unknown()).optional(),
  license_terms: z.record(z.unknown()).optional(),
  price_usd: z.number().optional(),
  expires_at: z.string().nullable().optional(),
  persist: z.boolean().default(true),
});
export type ExportSkillRequest = z.infer<typeof exportSkillRequestSchema>;

export const exportSkillResponseSchema = z.object({
  skill_id: z.string().uuid(),
  skill_version: z.string(),
  format: skillExportFormatSchema,
  target: skillExportTargetSchema,
  redaction_total_replacements: z.number(),
  redaction_by_pattern: z.record(z.number()),
  artifact_text: z.string(),
  artifact_hash: z.string(),
  export_id: z.string().uuid().nullable(),
});
export type ExportSkillResponse = z.infer<typeof exportSkillResponseSchema>;

// ── University KPI (root-page summary) ────────────────────────────────
export const universitySummarySchema = z.object({
  activeSkillsCount: z.number(),
  tunersCount: z.number(),
  trainingSessionsThisWeek: z.number(),
  trainingSessionsActive: z.number(),
  latestExportAt: z.string().nullable(),
  totalExports: z.number(),
  // Phase 9 — Self-learning extensions.
  abTestsActive: z.number().optional(),
  patternClustersCount: z.number().optional(),
  marketplacePublishedCount: z.number().optional(),
});
export type UniversitySummary = z.infer<typeof universitySummarySchema>;

// ════════════════════════════════════════════════════════════════════
// Phase 9 — M7 Self-learning: patterns + A/B tests + marketplace flow.
// ════════════════════════════════════════════════════════════════════

// ── Pattern detection ────────────────────────────────────────────────
export const patternClusterSchema = z.object({
  clusterId: z.number().int(),
  skillId: z.string().uuid().nullable(),
  skillKey: z.string().nullable(),
  skillName: z.string().nullable(),
  representativeText: z.string(),
  memberCount: z.number().int(),
  memberIds: z.array(z.string().uuid()),
  successRate: z.number().nullable(),
  avgIterations: z.number().nullable(),
  correctionKind: z.enum(['tuner', 'founder', 'mixed']),
  generatedAt: z.string(),
});
export type PatternCluster = z.infer<typeof patternClusterSchema>;

export const patternClustersResponseSchema = z.object({
  clusters: z.array(patternClusterSchema),
  totalAttemptsScanned: z.number().int(),
  windowDays: z.number().int(),
  generatedAt: z.string(),
});
export type PatternClustersResponse = z.infer<typeof patternClustersResponseSchema>;

// ── A/B tests ────────────────────────────────────────────────────────
export const abTestStatuses = [
  'proposed',
  'running',
  'evaluated',
  'promoted',
  'rejected',
  'aborted',
] as const;
export const abTestStatusSchema = z.enum(abTestStatuses);
export type AbTestStatus = z.infer<typeof abTestStatusSchema>;

export const abTestSchema = z.object({
  id: z.string().uuid(),
  skillId: z.string().uuid(),
  skillKey: z.string().nullable(),
  skillName: z.string().nullable(),
  controlVersion: z.string(),
  candidateVersion: z.string(),
  candidatePromptText: z.string().nullable(),
  trafficSplit: z.number(),
  status: abTestStatusSchema,
  startedAt: z.string(),
  endsAt: z.string(),
  endedAt: z.string().nullable(),
  metadata: z.record(z.unknown()),
});
export type AbTest = z.infer<typeof abTestSchema>;

export const abTestEvaluationSchema = z.object({
  experimentId: z.string().uuid(),
  controlN: z.number().int(),
  candidateN: z.number().int(),
  controlScore: z.number().nullable(),
  candidateScore: z.number().nullable(),
  relativeLift: z.number().nullable(),
  pValue: z.number().nullable(),
  significant: z.boolean(),
  decision: z.string(),
});
export type AbTestEvaluation = z.infer<typeof abTestEvaluationSchema>;

export const abTestsListSchema = z.object({
  tests: z.array(abTestSchema),
});
export type AbTestsList = z.infer<typeof abTestsListSchema>;

export const createAbTestRequestSchema = z.object({
  skillId: z.string().uuid(),
  controlVersion: z.string().min(1),
  candidateVersion: z.string().min(1),
  candidatePromptText: z.string().min(8),
  durationDays: z.number().int().min(1).max(180).default(14),
  trafficSplit: z.number().gt(0).lt(1).default(0.2),
  metadata: z.record(z.unknown()).optional(),
  dryRun: z.boolean().default(false),
});
export type CreateAbTestRequest = z.infer<typeof createAbTestRequestSchema>;

export const abTestDetailSchema = z.object({
  test: abTestSchema,
  evaluation: abTestEvaluationSchema.nullable(),
});
export type AbTestDetail = z.infer<typeof abTestDetailSchema>;

export const concludeAbTestRequestSchema = z.object({
  decision: z.enum(['promote', 'reject']),
  notes: z.string().optional(),
});
export type ConcludeAbTestRequest = z.infer<typeof concludeAbTestRequestSchema>;

// ── Marketplace + redaction-aware publish flow ───────────────────────
export const marketplaceSkillSchema = z.object({
  skillId: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  version: z.string(),
  description: z.string().nullable(),
  licenseType: z.string(),
  marketplaceStatus: skillMarketplaceStatusSchema,
  effectivenessScore: z.number().nullable(),
  totalAttempts: z.number().nullable(),
  lastExportAt: z.string().nullable(),
  exportCount: z.number().int(),
  publishedAt: z.string().nullable(),
  category: z.string().nullable(),
  installCount: z.number().int(),
});
export type MarketplaceSkill = z.infer<typeof marketplaceSkillSchema>;

export const marketplaceSkillsListSchema = z.object({
  skills: z.array(marketplaceSkillSchema),
});
export type MarketplaceSkillsList = z.infer<typeof marketplaceSkillsListSchema>;

export const redactionPreviewSchema = z.object({
  skillId: z.string().uuid(),
  skillVersion: z.string(),
  format: skillExportFormatSchema,
  originalText: z.string(),
  redactedText: z.string(),
  totalReplacements: z.number().int(),
  replacementsByPattern: z.record(z.number()),
  redactionHash: z.string(),
});
export type RedactionPreview = z.infer<typeof redactionPreviewSchema>;

export const publishToMarketplaceRequestSchema = z.object({
  skillId: z.string().uuid(),
  targetStatus: z.enum(['candidate', 'exportable', 'published']).default('published'),
  redactionAccepted: z.literal(true),
  redactionHash: z.string().min(8),
  category: z.string().optional(),
  notes: z.string().optional(),
});
export type PublishToMarketplaceRequest = z.infer<typeof publishToMarketplaceRequestSchema>;

export const publishToMarketplaceResponseSchema = z.object({
  skillId: z.string().uuid(),
  version: z.string(),
  newStatus: skillMarketplaceStatusSchema,
  previousStatus: skillMarketplaceStatusSchema,
  redactionHash: z.string(),
});
export type PublishToMarketplaceResponse = z.infer<typeof publishToMarketplaceResponseSchema>;

export const installMarketplaceSkillRequestSchema = z.object({
  skillId: z.string().uuid(),
});
export type InstallMarketplaceSkillRequest = z.infer<typeof installMarketplaceSkillRequestSchema>;

export const installMarketplaceSkillResponseSchema = z.object({
  installedSkillId: z.string().uuid(),
  installedVersion: z.string(),
  sourceSkillId: z.string().uuid(),
  installedAt: z.string(),
});
export type InstallMarketplaceSkillResponse = z.infer<typeof installMarketplaceSkillResponseSchema>;

// ── Similarity (lightweight wrapper) ─────────────────────────────────
export const similarPromptHitSchema = z.object({
  attemptId: z.string().uuid(),
  skillId: z.string().uuid().nullable(),
  skillKey: z.string().nullable(),
  skillName: z.string().nullable(),
  promptText: z.string(),
  outcome: z.string(),
  similarity: z.number(),
  modelKey: z.string().nullable(),
  costUsd: z.number().nullable(),
  createdAt: z.string(),
});
export type SimilarPromptHit = z.infer<typeof similarPromptHitSchema>;

export const similarPromptsResponseSchema = z.object({
  hits: z.array(similarPromptHitSchema),
  recommendations: z.array(
    z.object({
      skillId: z.string().uuid(),
      skillVersion: z.string().nullable(),
      support: z.number(),
      avgSimilarity: z.number(),
      bestModel: z.string().nullable(),
    }),
  ),
});
export type SimilarPromptsResponse = z.infer<typeof similarPromptsResponseSchema>;
